import { useState } from "react";
import { useSearchParams } from "react-router";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorState } from "@/components/ErrorState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  useConnectSignInMethod,
  useDisconnectSignInMethod,
  useSignInMethods,
} from "@/api/hooks/use-sign-in-methods";
import { PROVIDER_LABELS, requestPasswordReset, signInErrorMessage } from "@/lib/auth";
import { toUserMessage } from "@/lib/errors";

const rowCls = "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line-subtle bg-surface p-4";
const buttonCls =
  "rounded-md border border-line px-3 py-1.5 text-sm text-fg transition-colors hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-40";

/** Settings → Sign-in: how this Member can sign in. They connect GitHub or
 * Google, disconnect one while another way remains, and set a password by
 * email when they signed up without one. */
export function SignInMethods() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { data, isLoading, isError, refetch } = useSignInMethods();
  const connect = useConnectSignInMethod();
  const disconnect = useDisconnectSignInMethod();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState(() => signInErrorMessage(params.get("error"), params.get("via")));
  const [passwordLinkSent, setPasswordLinkSent] = useState(false);
  const justConnected = PROVIDER_LABELS.get(params.get("connected") ?? "");

  if (isLoading) return <LoadingSkeleton rows={3} />;
  if (isError || !data) return <ErrorState message="Couldn't load your sign-in methods." onRetry={() => refetch()} />;

  const connected = new Map(data.connected.map((c) => [c.provider, c]));
  const shown = [...PROVIDER_LABELS.keys()].filter((p) => data.available.includes(p) || connected.has(p));
  const methodCount = data.connected.length + (data.password ? 1 : 0);

  async function emailPasswordLink() {
    if (!user) return;
    setError("");
    try {
      await requestPasswordReset(user.email);
      setPasswordLinkSent(true);
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  function startConnect(provider: string) {
    setError("");
    connect.mutate(provider, { onError: (err) => setError(toUserMessage(err)) });
  }

  function confirmDisconnect() {
    if (!confirming) return;
    disconnect.mutate(confirming, {
      onError: (err) => setError(toUserMessage(err)),
      onSettled: () => setConfirming(null),
    });
  }

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-fg-muted">
        The ways you can sign in to Phantm. Each one opens this same account.
      </p>
      {justConnected && !error && (
        <div role="status" className="rounded-md border border-line-subtle bg-surface px-3 py-2 text-sm text-fg">
          {justConnected} is connected. You can sign in with it from now on.
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {error}
        </div>
      )}

      <div data-testid="sign-in-password" className={rowCls}>
        <div>
          <h2 className="text-sm font-medium text-fg">Password</h2>
          <p className="text-xs text-fg-muted">
            {data.password ? "Set." : "Not set: you signed up without one."}
          </p>
        </div>
        {!data.password &&
          (passwordLinkSent ? (
            <p className="text-xs text-fg-muted">Check your email for a link to set your password.</p>
          ) : (
            <button type="button" onClick={emailPasswordLink} className={buttonCls}>
              Email me a link to set one
            </button>
          ))}
      </div>

      {shown.map((provider) => {
        const label = PROVIDER_LABELS.get(provider);
        const link = connected.get(provider);
        const onlyWay = Boolean(link) && methodCount === 1;
        return (
          <div key={provider} data-testid={`sign-in-${provider}`} className={rowCls}>
            <div>
              <h2 className="text-sm font-medium text-fg">{label}</h2>
              <p className="text-xs text-fg-muted">
                {link ? `Connected${link.email ? ` as ${link.email}` : ""}.` : "Not connected."}
                {onlyWay && " This is your only way to sign in, so it can't be disconnected."}
              </p>
            </div>
            {link ? (
              <button
                type="button"
                disabled={onlyWay || disconnect.isPending}
                onClick={() => setConfirming(provider)}
                className={buttonCls}
              >
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                disabled={connect.isPending}
                onClick={() => startConnect(provider)}
                className={buttonCls}
              >
                Connect {label}
              </button>
            )}
          </div>
        );
      })}

      {confirming && (
        <ConfirmDialog
          title={`Disconnect ${PROVIDER_LABELS.get(confirming)}?`}
          message={`${PROVIDER_LABELS.get(confirming)} will be removed from your sign-in methods. Signing in with ${PROVIDER_LABELS.get(confirming)} later connects it again, if it uses the same email as this account.`}
          confirmLabel="Disconnect"
          isPending={disconnect.isPending}
          onConfirm={confirmDisconnect}
          onCancel={() => setConfirming(null)}
          variant="danger"
        />
      )}
    </div>
  );
}
